import { CLOSED } from 'constants/ssuStatuses';
import { ROLE_SYSTEM_ADMINISTRATOR } from 'constants/userRoles';
import { userHasRole } from 'services/auth';
import { date, object, string } from 'yup';

import { appInfo, i18nt } from '../../../../../../../../../root';
import { emailRegexp } from '../../../../../../../../../services/email';
import { NSPP_PATIENT } from '../../../../../../../../../services/i18n/namespaces';
import { PAYMENT_TYPE_DIGITAL, PAYMENT_TYPE_PHYSICAL } from '../../../../../constants';

import { addressSchema, phoneNumberSchema, phonesInfoSchema, ssnSchema } from './fieldSchemas';

const REQUIRED_FIELD_MESSAGE = i18nt('validation.requiredField', { ns: NSPP_PATIENT });

export const addPatientSsuValidationSchema = object({
  study: object({}).required(REQUIRED_FIELD_MESSAGE),
  site: object({})
    .required(REQUIRED_FIELD_MESSAGE)
    .test('as', i18nt('validation.ssuIsClosed', { ns: NSPP_PATIENT }), function(site) {
      return !(!userHasRole(ROLE_SYSTEM_ADMINISTRATOR) && site.siteStatus === CLOSED);
    }),
  source: object().required(REQUIRED_FIELD_MESSAGE),
  status: object().required(REQUIRED_FIELD_MESSAGE)
});

export const medicalRecordNumberValidationSchema = object({
  medicalRecordNumber: string().when(['$patientStipendFieldsRequired'], function(
    [patientStipendFieldsRequired],
    schema
  ) {
    if (patientStipendFieldsRequired) {
      return schema.required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT }));
    }
    return schema.nullable();
  })
});

const cardIdSchema = string()
  .nullable()
  .ifNotEmpty(string().length(13, i18nt('validation.pidFillCorrectly', { ns: NSPP_PATIENT })))
  .when(['$originalPID'], function([originalPID], schema) {
    const globalAddressesEnabled = appInfo?.features?.globalAddressesEnabled;
    if (originalPID && !globalAddressesEnabled) {
      return schema.required(i18nt('validation.pidFillCorrectly', { ns: NSPP_PATIENT }));
    }
    return schema;
  });

export const addValidationSchema = object({}).shape(
  {
    firstName: string().required(REQUIRED_FIELD_MESSAGE),
    lastName: string().required(REQUIRED_FIELD_MESSAGE),
    dob: date().required(REQUIRED_FIELD_MESSAGE),
    phonesInfo: phonesInfoSchema,
    address: addressSchema,
    email: string()
      .nullable()
      .ifNotEmpty(string().matches(emailRegexp, i18nt('validation.emailFillCorrectly', { ns: NSPP_PATIENT })))
      .when(['paymentType'], function([paymentType], schema) {
        if (paymentType === PAYMENT_TYPE_DIGITAL) {
          return schema.required(i18nt('validation.requiredWithDigitalPayment', { ns: NSPP_PATIENT }));
        }
        return schema;
      }),
    guardianEmail: string()
      .nullable()
      .ifNotEmpty(string().matches(emailRegexp, i18nt('validation.guardianEmailFillCorrectly', { ns: NSPP_PATIENT }))),
    guardianPhone: phoneNumberSchema,
    heightFt: string().when(['heightIn'], function([heightIn], schema) {
      if (heightIn) {
        return schema.required(i18nt('validation.heightFtRequired', { ns: NSPP_PATIENT }));
      }
      return schema;
    }),
    cardId: cardIdSchema,
    ssn: ssnSchema,
    paymentType: string()
      .oneOf([PAYMENT_TYPE_PHYSICAL, PAYMENT_TYPE_DIGITAL])
      .when(['digitalPaymentAllowed', 'cardId'], function([digitalPaymentAllowed, cardId], schema) {
        if (digitalPaymentAllowed && cardId) {
          return schema.required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT }));
        }
        return schema.nullable();
      })
  },
  [['paymentType', 'cardId']]
);
